import React from 'react';
import {
	Box,
	Card,
	Checkbox,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { InfoIcon } from 'components/icons';
import { useFormContext, Controller } from 'react-hook-form';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';

const parseInputInt = (value: string): number => {
	const n = parseInt(value, 10);
	return isNaN(n) || n < 0 ? 0 : n;
};

export const ViolationPolicySection: React.FC<{ loading: boolean }> = ({
	loading,
}) => {
	const { control, watch, setValue } = useFormContext<ParkingLocation>();
	const violationCheckType = watch('violationCheckType');
	const maxStayMinutes = watch('maxStayMinutes') || 0;

	const hoursPart = Math.floor(maxStayMinutes / 60);
	const minutesPart = maxStayMinutes % 60;

	const updateMaxStay = (hours: number, minutes: number) => {
		const total = hours * 60 + minutes;
		setValue('maxStayMinutes', total, {
			shouldValidate: true,
			shouldDirty: true,
		});
	};

	return (
		<Card sx={{ padding: 1 }}>
			<Box display="flex" flexDirection="column" gap={1}>
				<Box display="flex" gap={1}>
					<Typography variant="body2" fontWeight={300}>
						Violation Check Frequency
					</Typography>
					<Tooltip title="This defines how far back the system checks for violations.">
						<InfoIcon fontSize="small" />
					</Tooltip>
				</Box>

				<Controller
					control={control}
					name="violationCheckType"
					render={({ field }) => (
						<FormControl>
							<RadioGroup
								row
								{...field}
								sx={{ flexWrap: 'wrap', gap: 1 }}
							>
								<FormControlLabel
									value="fixedMonthly"
									control={<Radio />}
									label={
										<Typography variant="caption">
											Fixed Monthly
										</Typography>
									}
								/>
								<FormControlLabel
									value="rolling"
									control={<Radio />}
									label={
										<Typography variant="caption">
											Rolling Days
										</Typography>
									}
								/>
								<FormControlLabel
									value="hourlyExpiry"
									control={<Radio />}
									label={
										<Typography variant="caption">
											Hourly (Time Limit)
										</Typography>
									}
								/>
							</RadioGroup>
						</FormControl>
					)}
				/>

				{violationCheckType === 'rolling' && (
					<Box display="flex" alignItems="center" gap={1}>
						<Typography variant="body2" fontWeight={300}>
							Check last
						</Typography>

						<Controller
							control={control}
							name="violationCheckWindowDays"
							rules={{
								required: 'This field is required',
								validate: (value) =>
									(!isNaN(value ?? NaN) &&
										Number(value) > 0) ||
									'Must be a positive number',
							}}
							render={({ field, fieldState }) => (
								<TextField
									{...field}
									value={field.value ?? ''}
									onChange={(e) => {
										const numeric = parseInt(
											e.target.value,
											10,
										);
										field.onChange(
											isNaN(numeric)
												? ''
												: numeric > 1
												? numeric
												: 1,
										);
									}}
									variant="outlined"
									size="small"
									type="number"
									disabled={loading}
									sx={{ width: 80 }}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
								/>
							)}
						/>

						<Typography variant="body2" fontWeight={300}>
							days
						</Typography>
					</Box>
				)}

				{violationCheckType === 'fixedMonthly' && (
					<Typography
						variant="body2"
						fontWeight={300}
						color="warning.main"
					>
						Checks will reset at the start of each month.
					</Typography>
				)}

				{violationCheckType === 'hourlyExpiry' && (
					<>
					<Controller
						control={control}
						name="maxStayMinutes"
						rules={{
							validate: (value) =>
								(typeof value === 'number' && value > 0) ||
								'At least one of hours or minutes is required',
						}}
						render={({ fieldState }) => (
							<Box
								display="flex"
								flexDirection="column"
								gap={1}
							>
								<Box display="flex" alignItems="center" gap={1}>
									<Typography
										variant="body2"
										fontWeight={300}
									>
										Max stay
									</Typography>
									<TextField
										value={hoursPart || ''}
										onChange={(e) =>
											updateMaxStay(
												parseInputInt(e.target.value),
												minutesPart,
											)
										}
										variant="outlined"
										size="small"
										type="number"
										disabled={loading}
										sx={{ width: 80 }}
										inputProps={{ min: 0 }}
										error={!!fieldState.error}
									/>
									<Typography
										variant="body2"
										fontWeight={300}
									>
										hours
									</Typography>
									<TextField
										value={minutesPart || ''}
										onChange={(e) =>
											updateMaxStay(
												hoursPart,
												parseInputInt(e.target.value),
											)
										}
										variant="outlined"
										size="small"
										type="number"
										disabled={loading}
										sx={{ width: 80 }}
										inputProps={{ min: 0, max: 59 }}
										error={!!fieldState.error}
									/>
									<Typography
										variant="body2"
										fontWeight={300}
									>
										minutes
									</Typography>
								</Box>
								{fieldState.error && (
									<Typography
										variant="caption"
										color="error"
									>
										{fieldState.error.message}
									</Typography>
								)}
								<Typography
									variant="caption"
									fontWeight={300}
									color="text.secondary"
								>
									Violations are flagged when the same plate
									is re-scanned after the time limit.
								</Typography>
							</Box>
						)}
					/>

					<Controller
						control={control}
						name="enableOverstayNotifications"
						render={({ field }) => (
							<Box>
								<FormControlLabel
									control={
										<Checkbox
											checked={!!field.value}
											onChange={(e) =>
												field.onChange(e.target.checked)
											}
											disabled={loading}
										/>
									}
									label={
										<Typography variant="caption">
											Enable overstay notifications
										</Typography>
									}
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									component="div"
									sx={{ pl: 4, pr: 1, pb: 0.5 }}
								>
									Show overstay alerts for this lot in the
									notification bell when a vehicle exceeds the
									time limit.
								</Typography>
							</Box>
						)}
					/>
					</>
				)}
			</Box>
		</Card>
	);
};
